import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, concatMap } from 'rxjs/operators';
import * as CONSTANTS from '../../../constants';
import {
  AuthAccessTokenDetails,
  ILoginUserNamePasswordInput,
  LoginAuthWithCredentialsResponse,
  LoginOAuthParams,
  SwapAuthForAccessTokens
} from '../../../models';
import { transformSwapAuthTokenDetails } from '../login-remember-me-utils-service/initiate-login-utils';
import { InitiateLoginFacadeService } from './initiate-login-facade.service';
import { PostWithCredentialsFacadeHelperService } from './post-with-credentials-facade-helper.service';
@Injectable({
  providedIn: 'root'
})
export class PostWithCredentialsFacadeService extends PostWithCredentialsFacadeHelperService {
  postWithCredFlow(
    loginOAuthParams: LoginOAuthParams,
    requestInput: ILoginUserNamePasswordInput
  ): Observable<AuthAccessTokenDetails | string | boolean> {
    if (this.pkceCode.codeChallenge && this.pkceCode.codeVerifier) {
      loginOAuthParams.pkceCode = this.pkceCode;
    }
    if (!this.flowId) {
      return of(
        this.LoginRememberMeErrorUtil.checkRememberMeErrors(
          loginOAuthParams.oauthConfig,
          'ERROR_MSG_NO_FLOW_ID_GENERATED'
        )
      );
    } else {
      return this.loginPostWithCred
        .loginPostWithCredentials(
          loginOAuthParams.oauthConfig,
          this.flowId,
          requestInput
        )
        .pipe(
          concatMap((response: LoginAuthWithCredentialsResponse) => {
            if (response.status === CONSTANTS.SHOULD_REMEMBER_USER) {
              return this.loginPostWithRememberUser.loginPostWithRememberUser(
                loginOAuthParams.oauthConfig,
                response.id ? response.id : '',
                requestInput
              );
            } else if (response.code === CONSTANTS.VALIDATION_ERROR) {
              return throwError(response);
            } else {
              return throwError(response);
            }
          }),
          concatMap((response: LoginAuthWithCredentialsResponse) => {
            if (response.status === CONSTANTS.COMPLETED) {
              return this.loginRememberMeUtils.swapAuthCodeForToken(
                loginOAuthParams,
                response?.authorizeResponse?.code
              );
            } else if (
              response.status === CONSTANTS.REMEMBERED_DEVICE_DETAILS
            ) {
              this.assignRememberMeDetails(response);
              return this.loginPostWithContinue.loginPostWithContinue(
                loginOAuthParams.oauthConfig,
                response.id ? response.id : ''
              );
            } else {
              return throwError(response);
            }
          }),
          concatMap(
            (
              response:
                | LoginAuthWithCredentialsResponse
                | SwapAuthForAccessTokens
            ) => {
              if ('status' in response) {
                if (response.status === CONSTANTS.COMPLETED) {
                  return this.loginRememberMeUtils.swapAuthCodeForToken(
                    loginOAuthParams,
                    response?.authorizeResponse?.code
                  );
                } else {
                  return throwError(response);
                }
              } else if ('access_token' in response) {
                return of(response);
              } else {
                return throwError(response);
              }
            }
          ),
          concatMap((response) => {
            if ('access_token' in response) {
              this.loginRememberMeUtils.sendTokenServiceWorker(
                loginOAuthParams.oauthConfig,
                response
              );
              const authAccessTokenDetails =
                transformSwapAuthTokenDetails(response);
              if (this.encyToken && this.uid) {
                return of({
                  ...authAccessTokenDetails,
                  encyToken: this.encyToken,
                  uid: this.uid
                });
              } else {
                return of(authAccessTokenDetails);
              }
            } else {
              return throwError(response);
            }
          }),
          catchError((error) => {
            const initiateLoginFacade = this.injector.get(
              InitiateLoginFacadeService
            );
            if (
              !loginOAuthParams.pkceCode.codeChallenge &&
              !loginOAuthParams.pkceCode.codeVerifier
            ) {
              loginOAuthParams.pkceCode =
                this.loginRememberMeUtils.generateCodeChallenge();
            }
            if (error.error.code !== CONSTANTS.VALIDATION_ERROR) {
              return initiateLoginFacade.initiateLogin(
                loginOAuthParams,
                requestInput
              );
            } else {
              return of(
                this.LoginRememberMeErrorUtil.checkRememberMeErrors(
                  loginOAuthParams.oauthConfig,
                  error
                )
              );
            }
          })
        );
    }
  }

  assignRememberMeDetails(response: LoginAuthWithCredentialsResponse) {
    this.encyToken = response.encryptedToken ? response.encryptedToken : '';
    this.uid = response.fingerprint ? response.fingerprint : '';
  }
}
